import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Thermometer, Stethoscope, Baby, Scissors, Brain, Eye, Bone, Radio, TestTube, Activity, HeartPulse } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FixedWidgets from '../components/FixedWidgets';
import a from '../assets/accient.webp';
import ortho from '../assets/ortho.jpg';
import opd from '../assets/opd.jpg';
import uro from '../assets/uro.jpg';
import thyroid from '../assets/thyroid.jpg';

const DepartmentsPage = () => {
  const [selectedDept, setSelectedDept] = useState(null);

  const departments = [
    {
  id: 1,
  name: 'OPD/IPD',
  icon: Stethoscope,
  color: 'from-[#4A90A4] to-[#6FCF97]',
  image: opd,
  services: [
    'Critical Care & Inpatient Monitoring',
    'In-house Dialysis Unit',
    'Cardiovascular & Hypertension Clinic',
    'Endocrinology (Diabetes & Thyroid)',
    'Internal Medicine & Infectious Diseases',
    'Neurological Care & Stroke Rehab',
    'Clinical Toxicology & De-addiction'
  ]
},
    {
      id: 1,
      name: 'General Medicine',
      icon: Stethoscope,
      color: 'from-[#4A90A4] to-[#6FCF97]',
      image: 'https://images.unsplash.com/photo-1710074213379-2a9c2653046a',
      services: ['ICU Care', 'Hypertension', 'Diabetes/Thyroid', 'Infectious Diseases', 'Stroke', 'Poisoning']
    },
    {
  id: 7,
  name: 'Diabetes & Thyroid',
  icon: Thermometer, // Represents metabolic/temperature regulation
  color: 'from-[#f46b45] to-[#eea849]', // Warm "energy" colors
  image: thyroid,
  description: 'Specialized metabolic care focusing on glucose regulation, hormonal balance, and endocrine wellness.',
  services: [
    'Comprehensive Diabetes Management',
    'Thyroid Disorder Clinic (Hypo/Hyper)',
    'Gestational Diabetes Care',
    'Continuous Glucose Monitoring (CGM)',
    'Obesity & Weight Management',
    'Diabetic Foot & Neuropathy Care'
  ]
},
    {
      id: 2,
      name: 'Accident & Trauma',
      icon: Stethoscope,
      color: 'from-[#4A90A4] to-[#6FCF97]',
      image: a,
      services: ['Critical Care & ICU', 'Acute Renal Support', 'Metabolic Stabilization', 'Sepsis & Infection Control', 'Neurological Trauma & Stroke', 'Toxicology & Antidote Therapy']
    },
    {
      id: 3,
      name: 'General Surgery',
      icon: Scissors,
      color: 'from-[#6FCF97] to-[#4A90A4]',
      image: 'https://images.unsplash.com/photo-1640876777002-badf6aee5bcc',
      services: ['Laparoscopic Surgery (Endoscopy)', 'Surgery Laser for Piles', 'Fissure Treatment', 'Fistula Surgery']
    },
    {
  id: 3,
  name: 'Urology & Nephrology',
  icon: Activity, // Changed from Scissors to Activity or Droplets for a medical feel
  color: 'from-[#6FCF97] to-[#4A90A4]',
  image: uro, // Updated to a more clinical image
  services: [
    'Laser Stone Surgery (URSL/RIRS)',
    'Prostate Surgery (TURP)',
    'Laparoscopic Urology',
    'UTI & Bladder Management',
    'Treatment for Kidney Failures'
  ]
},
{
  id: 4, 
  name: 'Neuro Care',
  icon: Brain, 
  color: 'from-[#6A11CB] to-[#2575FC]', // Shifted to deeper blues/purples typically used for Neurology
  image: 'https://images.unsplash.com/photo-1559757175-5700dde675bc',
  services: [
    'Traumatic Brain Injury (TBI) Management',
    'Microscopic Spine Surgery',
    'Stroke Intervention & Rehabilitation',
    'Epilepsy & Seizure Disorders',
    'Headache & Migraine Clinic',
    'Neuro-Oncology (Brain Tumor Care)',
    'Parkinson’s & Movement Disorders'
  ]
},
{
  id: 5,
  name: 'Psychiatric Care',
  icon: HeartPulse, 
  color: 'from-[#11998e] to-[#38ef7d]', // Calming greens and teals
  image: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e',
  services: [
    'Depression & Anxiety Treatment',
    'De-addiction & Substance Abuse',
    'Schizophrenia & Psychosis Management',
    'Child & Adolescent Psychiatry',
    'Stress & Anger Management',
    'Bipolar Disorder Therapy',
    'Counseling & Psychotherapy'
  ]
},
    {
      id: 4,
      name: 'Obstetric & Gynaecology',
      icon: Baby,
      color: 'from-pink-500 to-purple-500',
      image: 'https://images.unsplash.com/photo-1758654860101-d9d3ae39e191',
      services: ['Antenatal Care/Scan', 'High-Risk Pregnancy', 'Infertility', 'Laparoscopic Gynaec Surgeries', 'Menopause', 'Menstrual Problems', 'Family Planning']
    },
    {
      id: 5,
      name: 'Paediatrics',
      icon: Baby,
      color: 'from-blue-400 to-cyan-400',
      image: 'https://images.unsplash.com/photo-1758691462666-6470b740f544',
      services: ['NICU', 'PICU', 'Vaccination', 'Allergy', 'Asthma', 'Pneumonia', 'Hormone/Kidney Problems']
    },
    {
      id: 6,
      name: 'Orthopedics',
      icon: Bone,
      color: 'from-orange-500 to-red-500',
      image: ortho,
      services: ['Knee/Hip Replacement', 'Trauma', 'Arthroscopy', 'Sports/Spine Injury']
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <FixedWidgets />

      {/* Hero Section */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden mt-20">
        <motion.div
          initial={{ scale: 1.2 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.5 }}
          className="absolute inset-0 z-0"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#1e3a5f]/90 to-[#4A90A4]/80 z-10" />
          <img
            src="https://images.unsplash.com/photo-1597807037496-c56a1d8bc29a"
            alt="Departments"
            className="w-full h-full object-cover"
          />
        </motion.div>

        <div className="relative z-20 max-w-7xl mx-auto px-6 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-5xl md:text-6xl font-bold text-white mb-6"
          >
            Our <span className="text-[#6FCF97]">Specialities</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl text-gray-200 max-w-3xl mx-auto"
          >
            Comprehensive medical care across multiple specialties
          </motion.p>
        </div>
      </section>

      {/* Departments Grid */}
      <section className="py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {departments.map((dept, index) => (
              <motion.div
                key={dept.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                whileHover={{ y: -10, scale: 1.02 }}
                onClick={() => setSelectedDept(dept)}
                className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer group"
              >
                <div className="relative h-48 overflow-hidden">
                  <motion.img
                    whileHover={{ scale: 1.1 }}
                    transition={{ duration: 0.5 }}
                    src={dept.image}
                    alt={dept.name}
                    className="w-full h-full object-cover"
                  />
                  <div className={`absolute inset-0 bg-gradient-to-t ${dept.color} opacity-70 group-hover:opacity-80 transition-opacity duration-300`} />
                  <motion.div
                    whileHover={{ rotate: 360, scale: 1.1 }}
                    transition={{ duration: 0.5 }}
                    className="absolute top-4 right-4 w-16 h-16 bg-white/20 backdrop-blur-md rounded-2xl flex items-center justify-center"
                  >
                    <dept.icon className="text-white" size={28} />
                  </motion.div>
                </div>

                <div className="p-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{dept.name}</h3>
                  <p className="text-gray-600 mb-4">
                    {dept.services.length} specialized services
                  </p>
                  <motion.button
                    whileHover={{ x: 5 }}
                    className="text-[#4A90A4] font-semibold flex items-center space-x-2 group"
                  >
                    <span>View Services</span>
                    <span className="group-hover:translate-x-1 transition-transform">→</span>
                  </motion.button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Department Details Modal */}
      <AnimatePresence>
        {selectedDept && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedDept(null)}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
            >
              <div className="relative h-64 overflow-hidden">
                <img
                  src={selectedDept.image}
                  alt={selectedDept.name}
                  className="w-full h-full object-cover"
                />
                <div className={`absolute inset-0 bg-gradient-to-t ${selectedDept.color} opacity-80`} />
                <div className="absolute inset-0 flex items-center justify-center">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
                    className="w-24 h-24 bg-white/20 backdrop-blur-md rounded-3xl flex items-center justify-center"
                  >
                    <selectedDept.icon className="text-white" size={48} />
                  </motion.div>
                </div>
                <button
                  onClick={() => setSelectedDept(null)}
                  className="absolute top-4 right-4 w-10 h-10 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors"
                >
                  ✕
                </button>
              </div>

              <div className="p-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">{selectedDept.name}</h2>
                <h3 className="text-xl font-semibold text-gray-700 mb-4">Our Services:</h3>
                <ul className="space-y-3">
                  {selectedDept.services.map((service, index) => (
                    <motion.li
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="flex items-center space-x-3 text-gray-600"
                    >
                      <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${selectedDept.color}`} />
                      <span>{service}</span>
                    </motion.li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <Footer />
    </div>
  );
};

export default DepartmentsPage;
